// frontend/src/pages/admin/AdminMarketplacePage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, Loader2, Search, CheckCircle, XCircle, Clock, 
  DollarSign, User, FileText, Calendar,
  RefreshCw, Gavel, FileSignature, TrendingUp,
  Users, Home
} from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import api from '../../services/api';
import config from '../../config';

export const AdminMarketplacePage = () => {
  const [activeTab, setActiveTab] = useState('bids');
  const [bids, setBids] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      
      if (activeTab === 'bids') {
        const response = await fetch(config.getApiUrl('/marketplace/bids/'), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const data = await response.json();
        setBids(data.results || []);
      } else if (activeTab === 'contracts') {
        const response = await fetch(config.getApiUrl('/marketplace/contracts/'), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const data = await response.json();
        setContracts(data.results || []);
      } else if (activeTab === 'commissions') {
        const response = await fetch(config.getApiUrl('/marketplace/commissions/'), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const data = await response.json();
        setCommissions(data.results || []);
      } else if (activeTab === 'availability') {
        const response = await fetch(config.getApiUrl('/marketplace/availability/'), {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        const data = await response.json();
        setAvailabilities(data.results || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getBidStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار', color: 'bg-yellow-100 text-yellow-800' },
      'accepted': { text: 'پذیرفته شده', color: 'bg-green-100 text-green-800' },
      'rejected': { text: 'رد شده', color: 'bg-red-100 text-red-800' },
      'expired': { text: 'منقضی شده', color: 'bg-gray-100 text-gray-800' },
    };
    const info = statusMap[status] || statusMap.pending;
    return <span className={`px-2 py-1 text-xs rounded-full ${info.color}`}>{info.text}</span>;
  };

  const getContractStatusBadge = (status) => {
    const statusMap = {
      'draft': { text: 'پیش‌نویس', color: 'bg-gray-100 text-gray-800' },
      'signed': { text: 'امضا شده', color: 'bg-blue-100 text-blue-800' },
      'in_progress': { text: 'در حال اجرا', color: 'bg-yellow-100 text-yellow-800' },
      'completed': { text: 'تکمیل شده', color: 'bg-green-100 text-green-800' },
      'cancelled': { text: 'لغو شده', color: 'bg-red-100 text-red-800' },
    };
    const info = statusMap[status] || statusMap.draft;
    return <span className={`px-2 py-1 text-xs rounded-full ${info.color}`}>{info.text}</span>;
  };

  const formatPrice = (price) => {
    if (!price) return '۰';
    return parseInt(price).toLocaleString('fa-IR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="بازارچه نصاب‌ها | پنل ادمین" noindex={true} />
      
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">بازارچه نصاب‌ها</h1>
            <p className="text-sm text-gray-500 mt-1">مدیریت پیشنهادات، قراردادها و کمیسیون‌ها</p>
          </div>
          <button
            onClick={() => { setRefreshing(true); fetchData(); }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            بروزرسانی
          </button>
        </div>

        {/* تب‌ها */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('bids')}
              className={`pb-3 px-1 text-sm font-medium transition ${
                activeTab === 'bids'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Gavel className="w-4 h-4 inline ml-1" />
              پیشنهادات
            </button>
            <button
              onClick={() => setActiveTab('contracts')}
              className={`pb-3 px-1 text-sm font-medium transition ${
                activeTab === 'contracts'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileSignature className="w-4 h-4 inline ml-1" />
              قراردادها
            </button>
            <button
              onClick={() => setActiveTab('commissions')}
              className={`pb-3 px-1 text-sm font-medium transition ${
                activeTab === 'commissions'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline ml-1" />
              کمیسیون‌ها
            </button>
            <button
              onClick={() => setActiveTab('availability')}
              className={`pb-3 px-1 text-sm font-medium transition ${
                activeTab === 'availability'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4 inline ml-1" />
              زمانبندی نصاب‌ها
            </button>
          </nav>
        </div>

        {/* تب پیشنهادات (Bids) */}
        {activeTab === 'bids' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نصاب</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مبلغ پیشنهادی</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مدت اجرا</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        پیشنهادی یافت نشد
                      </td>
                    </tr>
                  ) : (
                    bids.map((bid) => (
                      <tr key={bid.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-500">#{bid.id}</td>
                        <td className="px-6 py-4">{bid.installer?.company_name || '-'}</td>
                        <td className="px-6 py-4">{formatPrice(bid.proposed_cost)} تومان</td>
                        <td className="px-6 py-4">{bid.proposed_duration} روز</td>
                        <td className="px-6 py-4">{getBidStatusBadge(bid.status)}</td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {new Date(bid.created_at).toLocaleDateString('fa-IR')}
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-blue-600 hover:text-blue-800">
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* تب قراردادها (Contracts) */}
        {activeTab === 'contracts' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نصاب</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مبلغ قرارداد</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">امضای مشتری</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">امضای نصاب</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        قراردادی یافت نشد
                      </td>
                    </tr>
                  ) : (
                    contracts.map((contract) => (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-500">#{contract.id}</td>
                        <td className="px-6 py-4">{contract.bid?.installer?.company_name || '-'}</td>
                        <td className="px-6 py-4">{formatPrice(contract.bid?.proposed_cost)} تومان</td>
                        <td className="px-6 py-4">{getContractStatusBadge(contract.status)}</td>
                        <td className="px-6 py-4">
                          {contract.customer_signed ? '✓' : '✗'}
                        </td>
                        <td className="px-6 py-4">
                          {contract.installer_signed ? '✓' : '✗'}
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-blue-600 hover:text-blue-800">
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* تب کمیسیون‌ها (Commissions) */}
        {activeTab === 'commissions' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">شناسه</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">قرارداد</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">درصد</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مبلغ</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت پرداخت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        کمیسیونی یافت نشد
                      </td>
                    </tr>
                  ) : (
                    commissions.map((commission) => (
                      <tr key={commission.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-gray-500">#{commission.id}</td>
                        <td className="px-6 py-4">#{commission.contract}</td>
                        <td className="px-6 py-4">{commission.percentage}%</td>
                        <td className="px-6 py-4">{formatPrice(commission.amount)} تومان</td>
                        <td className="px-6 py-4">
                          {commission.is_paid ? (
                            <span className="text-green-600">پرداخت شده</span>
                          ) : (
                            <span className="text-yellow-600">در انتظار</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">
                          {new Date(commission.created_at).toLocaleDateString('fa-IR')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* تب زمانبندی نصاب‌ها (Installer Availability) */}
        {activeTab === 'availability' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نصاب</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">توضیحات</th>
                  </tr>
                </thead>
                <tbody>
                  {availabilities.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        اطلاعاتی یافت نشد
                      </td>
                    </tr>
                  ) : (
                    availabilities.map((avail) => (
                      <tr key={avail.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">{avail.installer?.company_name || '-'}</td>
                        <td className="px-6 py-4">{new Date(avail.date).toLocaleDateString('fa-IR')}</td>
                        <td className="px-6 py-4">
                          {avail.is_available ? (
                            <span className="text-green-600">آزاد</span>
                          ) : (
                            <span className="text-red-600">ناموجود</span>
                          )}
                        </td>
                        <td className="px-6 py-4 max-w-xs truncate">{avail.note || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};